import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, ApiRequestError } from "../api.ts";
import type { TeamInvite } from "@sportsos/engine";

// ── Invite QR Sheet (captain generates, shows QR) ──────────────────────────

interface InviteQRSheetProps {
  readonly teamId: string;
  readonly teamName: string;
  readonly onClose: () => void;
}

/**
 * Literal colours, never `var(--ds-…)`. The QR is drawn as SVG `fill` attributes, where a CSS
 * variable is not guaranteed to resolve, and a code whose colours fall back to black on black is
 * a square no camera can read. Always dark on white, in both themes, because that is what
 * scanners expect.
 */
const QR_DARK = "#051533";
const QR_LIGHT = "#ffffff";

export function InviteQRSheet({ teamId, teamName, onClose }: InviteQRSheetProps) {
  const [invite, setInvite] = useState<TeamInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadOrCreate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getTeamInvite(teamId).catch(() => null);
      if (res?.invite) {
        setInvite(res.invite);
      } else {
        const created = await api.createTeamInvite(teamId);
        setInvite(created.invite);
      }
    } catch (err: unknown) {
      // The reason, not just "it failed": a captain who is told "only the team owner can
      // invite" can act on it, and one told "could not generate" cannot.
      setError(
        err instanceof ApiRequestError
          ? `Could not generate invite: ${err.message}`
          : "Could not generate invite. Check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { loadOrCreate(); }, [loadOrCreate]);

  const inviteUrl = invite ? `${window.location.origin}/join/${invite.code}` : "";

  const handleCopy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
    } catch {
      const input = document.createElement("input");
      input.value = inviteUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!inviteUrl || !navigator.share) return;
    await navigator.share({
      title: `Join ${teamName}`,
      text: `Join ${teamName} on CricAI.co`,
      url: inviteUrl,
    }).catch(() => {});
  };

  return (
    <div className="ds-overlay-backdrop" onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 700,
      background: "var(--ds-overlay)", display: "flex",
      alignItems: "flex-end", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--ds-bg-elevated)",
        borderRadius: "var(--ds-radius-2xl) var(--ds-radius-2xl) 0 0",
        padding: "var(--ds-space-6)", width: "100%", maxWidth: 420,
        maxHeight: "85vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 className="ds-heading">Invite players</h2>
          <button className="ds-iconbtn ds-iconbtn--plain" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <p className="ds-caption" style={{ marginTop: "var(--ds-space-2)" }}>
          Players scan this QR code or tap the link to join <b>{teamName}</b>
        </p>

        {loading && (
          <div style={{ padding: "var(--ds-space-8)", textAlign: "center" }}>
            <span className="ds-caption">Generating invite...</span>
          </div>
        )}

        {error && (
          <div style={{ padding: "var(--ds-space-8)", textAlign: "center" }}>
            <span className="ds-caption" style={{ color: "var(--ds-danger-600)" }}>{error}</span>
            <button className="ds-btn ds-btn--secondary ds-btn--sm" onClick={loadOrCreate}
              style={{ marginTop: "var(--ds-space-3)" }}>Retry</button>
          </div>
        )}

        {invite && !loading && (
          <>
            <div style={{
              display: "flex", justifyContent: "center",
              padding: "var(--ds-space-6) 0",
            }}>
              <div style={{
                background: QR_LIGHT, padding: "var(--ds-space-5)",
                borderRadius: "var(--ds-radius-xl)", boxShadow: "var(--ds-shadow-md)",
              }}>
                <QRCodeSVG
                  value={inviteUrl}
                  size={200}
                  level="M"
                  bgColor={QR_LIGHT}
                  fgColor={QR_DARK}
                />
              </div>
            </div>

            <div className="ds-field" style={{ marginTop: "var(--ds-space-3)" }}>
              <label className="ds-label">Invite link</label>
              <div style={{ display: "flex", gap: "var(--ds-space-2)" }}>
                <input
                  className="ds-input"
                  readOnly
                  value={inviteUrl}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  style={{ flex: 1, fontSize: "var(--ds-text-xs)" }}
                />
                <button className="ds-btn ds-btn--secondary" onClick={handleCopy}>
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div style={{
              display: "flex", gap: "var(--ds-space-2)",
              marginTop: "var(--ds-space-4)",
            }}>
              {"share" in navigator && (
                <button className="ds-btn ds-btn--primary ds-btn--block" onClick={handleShare}>
                  Share invite
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Join Team Page (player scans QR, lands here) ──────────────────────────

interface JoinTeamPageProps {
  readonly code: string;
  readonly onJoined: () => void;
  readonly onBack: () => void;
}

export function JoinTeamPage({ code, onJoined, onBack }: JoinTeamPageProps) {
  const [teamName, setTeamName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    api.previewInvite(code)
      .then((res) => setTeamName(res.teamName))
      .catch(() => setError("Invite not found or expired"))
      .finally(() => setLoading(false));
  }, [code]);

  const handleJoin = async () => {
    setJoining(true);
    setError(null);
    try {
      await api.joinTeam(code);
      setJoined(true);
      setTimeout(onJoined, 1500);
    } catch {
      setError("Could not join team. Try again.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div style={{
      minHeight: "100dvh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "var(--ds-space-8)",
    }}>
      <div className="ds-card" style={{ width: "100%", maxWidth: 360, textAlign: "center" }}>
        {loading && <p className="ds-caption">Loading invite...</p>}

        {error && !loading && (
          <>
            <p style={{ color: "var(--ds-danger-600)", marginBottom: "var(--ds-space-4)" }}>{error}</p>
            <button className="ds-btn ds-btn--secondary ds-btn--block" onClick={onBack}>
              Go back
            </button>
          </>
        )}

        {teamName && !joined && !loading && (
          <>
            <div style={{ marginBottom: "var(--ds-space-5)" }}>
              <span className="ds-avatar ds-avatar--lg ds-avatar--amber" style={{
                width: 64, height: 64, fontSize: "var(--ds-text-xl)",
              }}>
                {teamName.slice(0, 2).toUpperCase()}
              </span>
            </div>
            <h2 className="ds-heading">Join {teamName}?</h2>
            <p className="ds-caption" style={{ marginTop: "var(--ds-space-2)" }}>
              You've been invited to join this team
            </p>
            <button
              className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
              onClick={handleJoin}
              disabled={joining}
              style={{ marginTop: "var(--ds-space-5)" }}
            >
              {joining ? "Joining..." : "Join team"}
            </button>
            <button
              className="ds-btn ds-btn--ghost ds-btn--block"
              onClick={onBack}
              style={{ marginTop: "var(--ds-space-2)" }}
            >
              Cancel
            </button>
          </>
        )}

        {joined && (
          <>
            <div style={{ fontSize: 48, marginBottom: "var(--ds-space-3)" }}>&#10003;</div>
            <h2 className="ds-heading">Joined {teamName}</h2>
            <p className="ds-caption" style={{ marginTop: "var(--ds-space-2)" }}>
              Redirecting...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
